/* eslint-disable no-unused-vars */
import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useProfiles } from '../context/ProfileContext'
import { useAuth } from '../context/AuthContext'
import './ProfileSelector.css'

function ProfileSelector() {
    const { user } = useAuth()
    const location = useLocation()
    const isDiscover = location.pathname === '/'
    const {
        profiles,
        activeProfile,
        activeProfileId,
        canCreateCustomProfile,
        switchProfile,
        createProfile,
        deleteProfile,
        customProfiles,
        defaultProfile
    } = useProfiles()

    const [confirmDelete, setConfirmDelete] = useState(false)
    const [showCreate, setShowCreate] = useState(false)
    const [newProfileName, setNewProfileName] = useState('')
    const [error, setError] = useState('')

    // When leaving Discover in scratch mode: save scratch state, switch to default
    // When returning to Discover: if scratch was saved, restore it
    useEffect(() => {
        if (!isDiscover && !activeProfileId && defaultProfile) {
            localStorage.setItem('marqueindex_last_profile', '__scratch__')
            switchProfile(defaultProfile.id)
        }
        if (isDiscover && localStorage.getItem('marqueindex_last_profile') === '__scratch__' && activeProfileId === defaultProfile?.id) {
            switchProfile(null)
        }
    }, [isDiscover, activeProfileId, defaultProfile, switchProfile])

    if (!user) return null

    const handleCreate = async () => {
        if (!newProfileName.trim()) return
        setError('')
        try {
            await createProfile(newProfileName.trim())
            setNewProfileName('')
            setShowCreate(false)
        } catch (e) {
            setError(e.message)
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleCreate()
        if (e.key === 'Escape') {
            setShowCreate(false)
            setNewProfileName('')
        }
    }

    return (
        <div className="profile-selector">
            <div className="profile-dropdown-wrapper">
                <select
                    className="profile-dropdown"
                    value={activeProfileId || ''}
                    onChange={e => {
                        const val = e.target.value
                        if (val === '__create__') {
                            setShowCreate(true)
                            e.target.value = activeProfileId || ''
                        } else if (val === '__scratch__') {
                            switchProfile(null)
                        } else if (val === '__delete__') {
                            setConfirmDelete(true)
                            e.target.value = activeProfileId || ''
                        } else {
                            localStorage.removeItem('marqueindex_last_profile')
                            switchProfile(val)
                        }
                    }}
                >
                    {isDiscover && (
                        <option value="__scratch__">Start from scratch</option>
                    )}
                    {profiles.map(p => (
                        <option key={p.id} value={p.id}>
                            {p.is_default ? 'My Brands' : p.name}
                        </option>
                    ))}
                    {canCreateCustomProfile && (
                        <option value="__create__">+ Create new profile</option>
                    )}
                    {activeProfile && !activeProfile.is_default && (
                        <option value="__delete__">— Delete this profile</option>
                    )}
                </select>
            </div>

            {confirmDelete && activeProfile && (
                <div className="delete-modal-backdrop" onClick={() => setConfirmDelete(false)}>
                    <div className="delete-modal" onClick={e => e.stopPropagation()}>
                        <p className="delete-modal-text">Delete "{activeProfile.name}"?</p>
                        <p className="delete-modal-sub">This will remove all saved and disliked brands in this profile.</p>
                        <div className="delete-modal-actions">
                            <button className="delete-confirm-no" onClick={() => setConfirmDelete(false)}>Cancel</button>
                            <button className="delete-confirm-yes" onClick={() => { deleteProfile(activeProfile.id); setConfirmDelete(false) }}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {showCreate && (
                <div className="delete-modal-backdrop" onClick={() => { setShowCreate(false); setNewProfileName(''); setError('') }}>
                    <div className="delete-modal" onClick={e => e.stopPropagation()}>
                        <p className="delete-modal-text">Create a new profile</p>
                        <p className="delete-modal-sub">Give your profile a name — e.g. Outdoor, Office, Casual.</p>
                        <input
                            type="text"
                            placeholder="Profile name"
                            value={newProfileName}
                            onChange={e => setNewProfileName(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="create-profile-modal-input"
                            autoFocus
                        />
                        {error && <p className="create-profile-modal-error">{error}</p>}
                        <div className="delete-modal-actions">
                            <button className="delete-confirm-no" onClick={() => { setShowCreate(false); setNewProfileName(''); setError('') }}>Cancel</button>
                            <button className="delete-confirm-yes" onClick={handleCreate}>Create</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ProfileSelector
